import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, STATUS_KEY, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveHazardSection } from "../components/live/LiveOpsSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  AlertTriangle, Droplets, Flame, Activity, Wind, Cloud,
  ChevronDown, ChevronUp, TrendingUp, Zap, Shield, Thermometer,
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const HAZARD_SCORES = [
  { hazard:"Crowd Stampede", score:78, trend:"up",   prev:62, color:"#EF4444", icon:"crowd" },
  { hazard:"Flood / Flash",  score:42, trend:"up",   prev:28, color:"#0891B2", icon:"flood" },
  { hazard:"Heat / Health",  score:64, trend:"up",   prev:52, color:"#F97316", icon:"heat"  },
  { hazard:"Fire Risk",      score:38, trend:"stable",prev:36, color:"#F59E0B", icon:"fire"  },
  { hazard:"Epidemic",       score:31, trend:"stable",prev:29, color:"#7C3AED", icon:"epi"   },
  { hazard:"Infra Failure",  score:28, trend:"down",  prev:34, color:"#64748B", icon:"infra" },
];

const COMPOSITE_RISK = 62;

// Zone names match the canonical spelling used across the rest of the
// platform (mockData.js → crowdFlowByZone) so translations and any future
// cross-page zone lookups resolve consistently — "Nashik Road" (no "Stn")
// was a local drift that silently fell through untranslated in Marathi.
const ZONE_RISK = [
  { zone:"Ramkund Ghat",     crowd:92, flood:28, heat:72, fire:22, total:76 },
  { zone:"Panchvati",        crowd:76, flood:32, heat:68, fire:18, total:62 },
  { zone:"Trimbakeshwar",    crowd:58, flood:44, heat:64, fire:28, total:54 },
  { zone:"Nashik Road Stn",  crowd:48, flood:38, heat:60, fire:24, total:46 },
  { zone:"Gangapur Camp",    crowd:42, flood:62, heat:58, fire:20, total:52 },
  { zone:"Kalaram Temple",   crowd:64, flood:24, heat:66, fire:26, total:58 },
];

// Zone name -> deep-dive page, for cross-linking the Zone × Hazard table.
const ZONE_LINK = "/crowd-intelligence";

const FLOOD_DATA = [
  { time:"06:00", level:3.2,  discharge:180, rainfall:0   },
  { time:"08:00", level:3.4,  discharge:196, rainfall:0   },
  { time:"10:00", level:3.6,  discharge:210, rainfall:2   },
  { time:"12:00", level:3.9,  discharge:228, rainfall:4   },
  { time:"14:00", level:4.2,  discharge:244, rainfall:6   },
  { time:"16:00", level:4.8,  discharge:272, rainfall:8   },
  { time:"18:00", level:5.4,  discharge:310, rainfall:12  },
];

const STAMPEDE_PREDICTION = [
  { hour:"14:00", ramkund:78, panchvati:52, trimbak:44, nashikRd:38 },
  { hour:"15:00", ramkund:82, panchvati:58, trimbak:48, nashikRd:42 },
  { hour:"16:00", ramkund:88, panchvati:64, trimbak:52, nashikRd:46 },
  { hour:"17:00", ramkund:92, panchvati:72, trimbak:58, nashikRd:52 },
  { hour:"18:00", ramkund:84, panchvati:78, trimbak:62, nashikRd:58 },
  { hour:"19:00", ramkund:72, panchvati:68, trimbak:56, nashikRd:52 },
];

const WEATHER_FORECAST = [
  { time:"15:00", temp:41, heatIndex:46, humidity:44, uv:9,  windKmh:8,  rainProb:15 },
  { time:"16:00", temp:40, heatIndex:44, humidity:46, uv:8,  windKmh:10, rainProb:22 },
  { time:"17:00", temp:38, heatIndex:42, humidity:48, uv:6,  windKmh:12, rainProb:35 },
  { time:"18:00", temp:36, heatIndex:39, humidity:52, uv:4,  windKmh:14, rainProb:48 },
  { time:"19:00", temp:34, heatIndex:36, humidity:56, uv:2,  windKmh:16, rainProb:55 },
  { time:"20:00", temp:32, heatIndex:34, humidity:60, uv:0,  windKmh:14, rainProb:40 },
];

const EPIDEMIC_WATCH = [
  { disease:"Heatstroke",        R0:"N/A", cases24h:44, trend:"up",   risk:"HIGH",   threshold:20  },
  { disease:"Gastroenteritis",   R0:"1.4", cases24h:12, trend:"up",   risk:"MED",    threshold:30  },
  { disease:"Respiratory (ARI)", R0:"1.8", cases24h:8,  trend:"stable",risk:"MED",   threshold:25  },
  { disease:"Cholera (watch)",   R0:"2.1", cases24h:0,  trend:"stable",risk:"LOW",   threshold:5   },
  { disease:"Dengue (vector)",   R0:"1.1", cases24h:2,  trend:"down",  risk:"LOW",   threshold:10  },
];

const COMPOUND_SCENARIOS = [
  {
    id:"SCN-A", name:"Crowd + Heat Compound Event",
    probability:28, severity:"CRITICAL",
    trigger:"Crowd density >9/m² + Heat index >46°C",
    expectedHour:"16:00–18:00",
    impacts:["Stampede risk at Ramkund","Mass heatstroke cases","Ambulance demand surge +200%"],
    mitigations:["Deploy crowd dispersal at 15:30","Activate 4 additional medical posts","Issue advisory 90 min before"],
  },
  {
    id:"SCN-B", name:"Flash Flood + Crowd Displacement",
    probability:14, severity:"HIGH",
    trigger:"Gangapur Dam discharge >350 cumecs + rainfall >15mm/hr",
    expectedHour:"18:00–21:00",
    impacts:["Godavari Ghat evacuation","Camp 3 inundation risk","Road closures — 4 routes"],
    mitigations:["Pre-position 2 NDRF platoons","Open overflow shelter OFZ-A","Halt Godavari Ghat access at 17:00"],
  },
  {
    id:"SCN-C", name:"Power Grid + Comms Failure",
    probability:8, severity:"HIGH",
    trigger:"Substation overload + generator failure Zone B",
    expectedHour:"Anytime",
    impacts:["Maha Kumbh AI monitoring blackout 12 min","Crowd management loss","Emergency services impact"],
    mitigations:["DG backup pre-armed","VSAT backup comms live","Manual protocols pre-briefed"],
  },
];

const INFRA_RISK = [
  { asset:"Godavari Bridge",     load:88, capacity:100, risk:72, status:"Monitor"  },
  { asset:"Trimbak Road",        load:76, capacity:100, risk:58, status:"OK"       },
  { asset:"MSEDCL Feeder Z-A",   load:84, capacity:100, risk:66, status:"Monitor"  },
  { asset:"Water Pumping Stn 8", load:92, capacity:100, risk:74, status:"Monitor"  },
  { asset:"BSNL Tower TK-04",    load:68, capacity:100, risk:44, status:"OK"       },
];

// CRITICAL/HIGH/MED/(LOW|OK) severity vocabulary collapses onto the shared
// STATUS_KEY buckets (critical/serious/warning/good) so a "CRITICAL" chip
// here reads identically to every other page's critical badge.
const RISK_BUCKET = { CRITICAL: STATUS_KEY.critical, HIGH: STATUS_KEY.serious, MED: STATUS_KEY.warning };
const riskColor = s => statusClasses(RISK_BUCKET[s] || STATUS_KEY.good);

const trendIcon = t =>
  t === "up" ? <TrendingUp size={12} className="text-red-500" /> :
  t === "down" ? <TrendingUp size={12} className="text-emerald-500 rotate-180" /> :
  <span className="text-[9px] text-slate-400">—</span>;

// Looks a hazard's score up by name so the KPI row can never drift from
// HAZARD_SCORES — the array rendered as gauge cards just below it.
const hazardScore = name => HAZARD_SCORES.find(h => h.hazard === name)?.score;

// ── component ────────────────────────────────────────────────────
export default function RiskIntelligence() {
  const { hazards } = useIntelligence({ running: false });
  const [expandedScn, setExpandedScn] = useState(null);
  const t = useTranslate();

  return (
    <div className="space-y-8">
      <SectionHeader icon="Radar" title="Multi-Hazard Predictive Risk Intelligence"
        subtitle="Composite risk engine · flood · stampede · heat · epidemic · fire · infrastructure · compound scenarios · weather · AI-driven 6-hour forecast — Maha AI · Simhastha 2027"
        badge="RISK: 62/100"
      />


      <LiveHazardSection hazards={hazards} />

      <StaticContentDivider />
      {/* KPIs — values looked up from HAZARD_SCORES / COMPOSITE_RISK, the
          same data driving the banner chips and gauge cards below, so this
          row can't silently drift from them. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Composite Risk"     value={`${COMPOSITE_RISK} / 100`}               icon="AlertTriangle" color="amber"  index={0} live />
        <MetricCard title="Crowd Stampede"     value={`${hazardScore("Crowd Stampede")} / 100`} icon="Users"         color="red"    index={1} live />
        <MetricCard title="Flood Risk"         value={`${hazardScore("Flood / Flash")} / 100`}  icon="Droplets"      color="blue"   index={2} live />
        <MetricCard title="Heat / Health"      value={`${hazardScore("Heat / Health")} / 100`}  icon="Thermometer"   color="orange" index={3} live />
        <MetricCard title="Fire Risk"          value={`${hazardScore("Fire Risk")} / 100`}      icon="Flame"         color="amber"  index={4} />
        <MetricCard title="Epidemic Risk"      value={`${hazardScore("Epidemic")} / 100`}       icon="Activity"      color="purple" index={5} />
        <MetricCard title="Infra Risk"         value={`${hazardScore("Infra Failure")} / 100`}  icon="Zap"           color="blue"   index={6} />
        <MetricCard title="Rain Probability"   value={`${WEATHER_FORECAST.find(w => w.time === "17:00").rainProb}% @17h`} icon="Cloud" color="blue" index={7} />
      </div>

      {/* Risk Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-red-500 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">
                {t("MAHA AI — MULTI-HAZARD RISK ENGINE · AI FORECAST HORIZON 6H · 14:35 IST")}
              </span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Composite Risk: 62/100 — ELEVATED")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("Crowd risk dominant (78) · heat index 46°C (peak 16:00–17:00) · Godavari level 4.2m ↑ · 1 compound scenario at 28% probability · All hazard teams on alert")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {HAZARD_SCORES.map((h, i) => {
              const c = googleColor(i);
              return (
                <div key={h.hazard} className="rounded-xl px-2.5 py-2 text-center min-w-[70px] shadow-sm"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-[8px] mb-0.5 tracking-wide font-semibold" style={{ color:c.text }}>{t(h.hazard.split(" ")[0])}</div>
                  <div className="text-sm font-black" style={{ color:c.text }}>{h.score}</div>
                  <div className="flex items-center justify-center">{trendIcon(h.trend)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Hazard Score Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {HAZARD_SCORES.map((h, i) => (
          <GaugeCard key={h.hazard} title={h.hazard} value={h.score} subtitle={`${t("Prev")}: ${h.prev}`} index={i} />
        ))}
      </div>

      {/* Zone Risk Matrix */}
      <ChartCard title="Zone × Hazard Risk Matrix"
        subtitle="Risk scores per zone across all 4 primary hazards (crowd · flood · heat · fire)">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                <th className="text-center text-red-700">{t("Crowd")}</th>
                <th className="text-center text-blue-700">{t("Flood")}</th>
                <th className="text-center text-orange-700">{t("Heat")}</th>
                <th className="text-center text-amber-700">{t("Fire")}</th>
                <th className="text-center font-black">{t("Composite")}</th>
              </tr>
            </thead>
            <tbody>
              {ZONE_RISK.map((z, i) => (
                <motion.tr key={z.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                  <td className="font-black text-slate-900">
                    <Link to={ZONE_LINK} className="hover:text-blue-700 hover:underline underline-offset-2">{t(z.zone)}</Link>
                  </td>
                  {[z.crowd, z.flood, z.heat, z.fire].map((v, j) => (
                    <td key={j} className="text-center">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${statusClasses(thresholdStatus(v, [
                        { min: 70, bucket: STATUS_KEY.critical },
                        { min: 50, bucket: STATUS_KEY.warning },
                        { min: 0,  bucket: STATUS_KEY.good },
                      ]))}`}>{v}</span>
                    </td>
                  ))}
                  <td className="text-center">
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full ${statusClasses(thresholdStatus(z.total, [
                      { min: 70, bucket: STATUS_KEY.critical },
                      { min: 50, bucket: STATUS_KEY.warning },
                      { min: 0,  bucket: STATUS_KEY.good },
                    ]))}`}>{z.total}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Stampede Prediction + Flood */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="AI Stampede Risk Forecast — Next 6 Hours"
          subtitle="Zone-wise crowd stampede probability (0–100) · AI confidence 89%" live>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={STAMPEDE_PREDICTION} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="hour" tick={{ ...axisTick, fontSize:9 }} />
              <YAxis domain={[0,100]} tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} formatter={(value) => t(value)} />
              <Line dataKey="ramkund"   stroke={chartColor(0)} strokeWidth={2.5} dot={false} name="Ramkund" />
              <Line dataKey="panchvati" stroke={chartColor(1)} strokeWidth={2}   dot={false} name="Panchvati" />
              <Line dataKey="trimbak"   stroke={chartColor(3)} strokeWidth={1.5} dot={false} name="Trimbak" />
              <Line dataKey="nashikRd"  stroke={chartColor(2)} strokeWidth={1.5} dot={false} name="Nashik Rd" />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[9px] text-violet-600 font-bold mt-2">
            {t("⚠ AI prediction — human review required before evacuation orders. DPDP 2023 compliant.")}
          </p>
          <Link to="/ai-assurance" className="text-[9px] font-bold text-blue-700 hover:text-blue-800 mt-1 inline-block">
            {t("→ View this model's accuracy record")}
          </Link>
        </ChartCard>

        <ChartCard title="Godavari River — Level & Discharge Forecast"
          subtitle="Gangapur Dam discharge · river level (m) · upstream rainfall — Nashik" live>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={FLOOD_DATA} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="floodGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={{ ...axisTick, fontSize:9 }} />
              <YAxis yAxisId="l" tick={{ ...axisTick, fontSize:9 }} />
              <YAxis yAxisId="r" orientation="right" tick={{ ...axisTick, fontSize:9 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} formatter={(value) => t(value)} />
              <Area yAxisId="l" type="monotone" dataKey="discharge" fill="url(#floodGrad)" stroke={chartColor(0)} strokeWidth={2} name="Discharge (cumecs)" />
              <Line yAxisId="r" type="monotone" dataKey="level"     stroke={chartColor(1)} strokeWidth={2.5} dot={false} name="River Level (m)" />
              <Bar  yAxisId="r" dataKey="rainfall" fill={chartColor(2)} radius={[2,2,0,0]} name="Rainfall (mm)" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-2 flex items-center gap-3 text-[9px] flex-wrap">
            <span className="text-amber-700 font-bold">{t("⚠ Alert Level: 5.0m")}</span>
            <span className="text-red-700 font-bold">{t("Critical Level: 6.0m")}</span>
            <span className="text-slate-500">{t("Current:")} <strong className="text-blue-700">4.2m</strong></span>
          </div>
        </ChartCard>
      </div>

      {/* Weather Forecast */}
      <ChartCard title="IMD Weather Impact Forecast — Nashik (Next 6 Hours)"
        subtitle="Temperature · heat index · UV · wind speed · rainfall probability">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th>{t("Time")}</th>
                <th className="text-center text-orange-700">{t("Temp °C")}</th>
                <th className="text-center text-red-700">{t("Heat Index")}</th>
                <th className="text-center text-blue-700">{t("Humidity %")}</th>
                <th className="text-center text-amber-700">{t("UV Index")}</th>
                <th className="text-center">{t("Wind km/h")}</th>
                <th className="text-center text-blue-700">{t("Rain %")}</th>
              </tr>
            </thead>
            <tbody>
              {WEATHER_FORECAST.map((w, i) => (
                <motion.tr key={w.time} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-mono font-bold text-slate-700">{w.time}</td>
                  <td className="text-center">
                    <span className={`font-black ${STATUS[thresholdStatus(w.temp, [
                      { min: 40, bucket: STATUS_KEY.critical },
                      { min: 36, bucket: STATUS_KEY.serious },
                      { min: 0,  bucket: STATUS_KEY.warning },
                    ])].text}`}>{w.temp}°C</span>
                  </td>
                  <td className="text-center">
                    <span className={`font-black ${STATUS[thresholdStatus(w.heatIndex, [
                      { min: 44, bucket: STATUS_KEY.critical },
                      { min: 0,  bucket: STATUS_KEY.serious },
                    ])].text}`}>{w.heatIndex}°C</span>
                  </td>
                  <td className="text-center text-slate-600">{w.humidity}%</td>
                  <td className="text-center">
                    <span className={`font-black ${STATUS[thresholdStatus(w.uv, [
                      { min: 8, bucket: STATUS_KEY.critical },
                      { min: 5, bucket: STATUS_KEY.warning },
                      { min: 0, bucket: STATUS_KEY.good },
                    ])].text}`}>{w.uv}</span>
                  </td>
                  <td className="text-center text-slate-600">{w.windKmh} km/h</td>
                  <td className="text-center">
                    <span className={`font-black ${STATUS[thresholdStatus(w.rainProb, [
                      { min: 40, bucket: STATUS_KEY.info },
                      { min: 0,  bucket: STATUS_KEY.neutral },
                    ])].text}`}>{w.rainProb}%</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Epidemic Watch */}
      <ChartCard title="Epidemic & Disease Surveillance — Risk Watch"
        subtitle="Active surveillance across 5 disease vectors · R₀ estimates · case trend">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Disease")}</th>
                <th className="text-center">R₀</th>
                <th className="text-center">{t("Cases 24h")}</th>
                <th className="text-center">{t("Trend")}</th>
                <th className="text-center">{t("Threshold")}</th>
                <th className="text-center">{t("Risk")}</th>
                <th>{t("Load vs Threshold")}</th>
              </tr>
            </thead>
            <tbody>
              {EPIDEMIC_WATCH.map((e, i) => (
                <motion.tr key={e.disease} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.07 }}>
                  <td className="font-bold text-slate-900">{t(e.disease)}</td>
                  <td className="text-center font-mono text-slate-600">{e.R0}</td>
                  <td className="text-center font-black text-slate-800">{e.cases24h}</td>
                  <td className="text-center">{trendIcon(e.trend)}</td>
                  <td className="text-center text-slate-500">{e.threshold}</td>
                  <td className="text-center">
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${riskColor(e.risk)}`}>{t(e.risk)}</span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className={`h-1.5 rounded-full ${STATUS[thresholdStatus(e.cases24h / e.threshold, [
                          { min: 1,   bucket: STATUS_KEY.critical },
                          { min: 0.6, bucket: STATUS_KEY.warning },
                          { min: 0,   bucket: STATUS_KEY.good },
                        ])].bar}`} initial={{ width:0 }}
                          animate={{ width:`${Math.min((e.cases24h / e.threshold) * 100, 100)}%` }}
                          transition={{ duration:0.8 }} />
                      </div>
                      <span className="text-[9px] font-black text-slate-600">{Math.round((e.cases24h/e.threshold)*100)}%</span>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Compound Scenarios */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-3">{t("Compound Risk Scenarios — Pre-Modelled")}</h3>
        <div className="space-y-2">
          {COMPOUND_SCENARIOS.map((s, i) => {
            const isOpen = expandedScn === s.id;
            return (
              <motion.div key={s.id}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`rounded-2xl border cursor-pointer transition-all ${
                  s.severity === "CRITICAL" ? `${STATUS.critical.border} bg-red-50/10` : `${STATUS.serious.border} bg-yellow-50/10`
                } ${isOpen ? "shadow-card" : ""}`}
                onClick={() => setExpandedScn(isOpen ? null : s.id)}
              >
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-[9px] text-slate-400">{s.id}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${riskColor(s.severity)}`}>{t(s.severity)}</span>
                      <span className="text-[9px] font-bold text-violet-600">{t("AI prob:")} {s.probability}%</span>
                    </div>
                    <p className="text-sm font-black text-slate-900">{t(s.name)}</p>
                    <p className="text-[10px] text-amber-700 mt-0.5">{t("Trigger:")} {t(s.trigger)}</p>
                    <p className="text-[9px] text-slate-500">{t("Window:")} {t(s.expectedHour)}</p>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white/60 rounded-b-2xl">
                        <div>
                          <div className="text-[9px] font-black text-red-600 uppercase mb-2">{t("Expected Impacts")}</div>
                          <div className="space-y-1.5">
                            {s.impacts.map((imp, idx) => (
                              <div key={idx} className="flex items-start gap-1.5">
                                <AlertTriangle size={10} className="text-red-500 flex-shrink-0 mt-0.5" />
                                <span className="text-[10px] text-red-700">{t(imp)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-emerald-600 uppercase mb-2">{t("Pre-Planned Mitigations")}</div>
                          <div className="space-y-1.5">
                            {s.mitigations.map((m, idx) => (
                              <div key={idx} className="flex items-start gap-1.5">
                                <Shield size={10} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                                <span className="text-[10px] text-emerald-700">{t(m)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Infrastructure Risk */}
      <ChartCard title="Critical Infrastructure Risk Monitor"
        subtitle="Load vs capacity for 5 key assets — alert above 80% load">
        <div className="space-y-3">
          {INFRA_RISK.map((r, i) => (
            <motion.div key={r.asset}
              initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
              className={`p-3 rounded-xl border ${r.load >= 85 ? `${STATUS.warning.border} bg-yellow-50/20` : "border-slate-200"}`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-black text-slate-900">{t(r.asset)}</span>
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                      r.status === "Monitor" ? `${STATUS.warning.bg} ${STATUS.warning.text}` : `${STATUS.good.bg} ${STATUS.good.text}`
                    }`}>{t(r.status)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div className={`h-2 rounded-full ${STATUS[thresholdStatus(r.load, [
                        { min: 85, bucket: STATUS_KEY.critical },
                        { min: 70, bucket: STATUS_KEY.warning },
                        { min: 0,  bucket: STATUS_KEY.good },
                      ])].bar}`}
                        initial={{ width:0 }} animate={{ width:`${r.load}%` }} transition={{ duration:0.8 }} />
                    </div>
                    <span className="text-[10px] font-black text-slate-700">{r.load}%</span>
                  </div>
                </div>
                <div className="text-center flex-shrink-0">
                  <div className={`text-xl font-black ${STATUS[thresholdStatus(r.risk, [
                    { min: 65, bucket: STATUS_KEY.critical },
                    { min: 50, bucket: STATUS_KEY.warning },
                    { min: 0,  bucket: STATUS_KEY.good },
                  ])].text}`}>{r.risk}</div>
                  <div className="text-[9px] text-slate-500">{t("risk score")}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
}






